import { useState, useMemo } from 'react';

import { EmptyStateMessage, Section, SectionCard, SectionHeader } from '@/components/common';
import { SkillAreaCard } from './SkillAreaCard';
import type { AppraisalDetailResponse } from '@/api/types/appraisal.types';
import type { RecordAccess } from '@/auth/permissions.types';
import { readOnlySectionVariant } from '@/auth/sectionReadOnlyStyle';

interface SkillAreaStrengthsSectionProps {
  appraisalDetail: AppraisalDetailResponse;
  permissions: RecordAccess;
  validationTriggered?: boolean;
}

export function SkillAreaStrengthsSection({
  appraisalDetail,
  permissions,
  validationTriggered = false,
}: SkillAreaStrengthsSectionProps) {
  const { skillAreaFeedback } = appraisalDetail;

  const sectionAccess = permissions.sections.skillAreaStrengths;
  const canEdit = sectionAccess?.access === 'edit';
  const readOnlyVariant = readOnlySectionVariant('skillAreaStrengths', permissions.role);

  const sortedAreas = useMemo(
    () => [...(skillAreaFeedback ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [skillAreaFeedback]
  );

  const hasError = validationTriggered && sortedAreas.some((area) => area.rating === null);

  const isHiddenForReadOnly = !canEdit && sortedAreas.length === 0;

  const [expandedAreas, setExpandedAreas] = useState<Set<number>>(new Set());

  const toggleAreaExpand = (areaId: number) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  if (isHiddenForReadOnly) {
    return null;
  }

  return (
    <Section>
      <SectionHeader error={hasError}>
        STRENGTHS &amp; DEVELOPMENT NEEDS BY SKILL AREA
      </SectionHeader>
      <SectionCard error={hasError}>
        {sortedAreas.length === 0 ? (
          <EmptyStateMessage message="No skill area feedback available." />
        ) : (
          sortedAreas.map((area, index) => (
            <SkillAreaCard
              key={area.skillAreaId}
              area={area}
              index={index}
              appraisalId={appraisalDetail.id}
              canEdit={canEdit}
              hasError={hasError}
              isExpanded={expandedAreas.has(area.skillAreaId)}
              onToggleExpand={() => toggleAreaExpand(area.skillAreaId)}
              rowVersion={appraisalDetail.rowVersion}
              readOnlyVariant={readOnlyVariant}
            />
          ))
        )}
      </SectionCard>
    </Section>
  );
}

export default SkillAreaStrengthsSection;
